// @ts-nocheck — Map components: @fortawesome/react-fontawesome and geocloud/GeomUtils lack TS declarations (consistent with SmartMap.tsx, MeasureTool.tsx)
// W12.3: migrated from AntD class (Form.create HOC) to Mantine functional component
// Removed: FormItemInput (FormItem/FormItemInput.jsx), antd Popover/Form/Button/Radio/notification
import React, { useState } from "react";
import { Popover, TextInput, Button, Radio, Group, Stack } from "@mantine/core";
import { IconCrosshair, IconX } from "@tabler/icons-react";
const FontAwesomeIcon = ({ icon }: { icon: string }) => {
  if (icon === "crosshairs") return <IconCrosshair size={14} />;
  if (icon === "times") return <IconX size={14} />;
  return null;
};
import { notifications } from "@mantine/notifications";
import GeomUtils from "../geocloud/GeomUtils";

// GMS format: accepts progressive partial input like -12:34:56,789
const gmsFormat =
  /^((-?)|(-|\d)|(-?\d)|(-?\d\d)|(-?\d\d:|-?\d:)|(-?\d\d:\d|-?\d:\d)|(-?\d\d:[0-5]\d|-?\d:[0-5]\d)|(-?\d\d:[0-5]\d:|-?\d:[0-5]\d:)|(-?\d\d:\d:|-?\d:\d:)|(-?\d\d:[0-5]\d:[0-5]|-?\d:[0-5]\d:[0-5])|(-?\d\d:\d:[0-5]|-?\d:\d:[0-5])|(-?\d\d:[0-5]\d:\d|-?\d:[0-5]\d:\d)|(-?\d\d:\d:\d|-?\d:\d:\d)|(-?\d\d:[0-5]\d:[0-5]\d|-?\d:[0-5]\d:[0-5]\d)|(-?\d\d:\d:[0-5]\d|-?\d:\d:[0-5]\d)|(-?\d\d:[0-5]\d:[0-5]\d\.|-?\d:[0-5]\d:[0-5]\d\.)|(-?\d\d:\d:[0-5]\d\.|-?\d:\d:[0-5]\d\.)|(-?\d\d:[0-5]\d:\d\.|-?\d:[0-5]\d:\d\.)|(-?\d\d:\d:\d\.|-?\d:\d:\d\.)|(-?\d\d:[0-5]\d:[0-5]\d,\d{0,4}|-?\d:[0-5]\d:[0-5]\d,\d{0,4})|(-?\d\d:\d:[0-5]\d,\d{0,4}|-?\d:\d:[0-5]\d,\d{0,4})|(-?\d\d:[0-5]\d:\d,\d{0,4}|-?\d:[0-5]\d:\d,\d{0,4})|(-?\d\d:\d:\d,\d{0,4}|-?\d:\d:\d,\d{0,4}))$/;

const decimalFormat = /^-{0,}\d{0,},{0,}\d{0,}$/;

interface GoToCoordinatesToolProps {
  mapKey?: string;
  getMap: () => any;
  setMarker?: (center: number[]) => void;
  clearMarker?: () => void;
  onPreActivate?: () => void;
}

type CoordFormat = "gdec" | "gms";

interface FormValues {
  lngGdec: string;
  latGdec: string;
  lngGms: string;
  latGms: string;
}

function gmsToGdec(ordinate: string): number | null {
  if (!ordinate) return null;
  const aux = (ordinate + ":0:0:0").split(":");
  const sign = aux[0].indexOf("-") >= 0 ? -1 : 1;
  const deg = parseInt("0" + aux[0].replace("-", ""));
  const min = parseInt("0" + aux[1]);
  const sec = parseFloat("0" + aux[2].replace(",", "."));
  if (isNaN(deg) || isNaN(min) || isNaN(sec)) return null;
  return sign * (deg + min / 60 + sec / 3600);
}

export default function GoToCoordinatesTool({
  getMap,
  setMarker,
  clearMarker,
  onPreActivate,
}: GoToCoordinatesToolProps) {
  const [opened, setOpened] = useState(false);
  const [format, setFormat] = useState<CoordFormat>("gdec");
  const [values, setValues] = useState<FormValues>({
    lngGdec: "",
    latGdec: "",
    lngGms: "",
    latGms: "",
  });

  const mapRef = React.useRef<any>(null);

  function handleOpen() {
    if (onPreActivate) onPreActivate();
    mapRef.current = getMap();
    setOpened(true);
  }

  function handleClose() {
    if (clearMarker) clearMarker();
    mapRef.current = null;
    setValues({ lngGdec: "", latGdec: "", lngGms: "", latGms: "" });
    setOpened(false);
  }

  function handleChange(field: keyof FormValues, raw: string) {
    // Apply mask per field/format
    if (field === "lngGdec" || field === "latGdec") {
      if (raw === "" || decimalFormat.test(raw)) {
        setValues((v) => ({ ...v, [field]: raw }));
      }
      return;
    }
    if (field === "lngGms" || field === "latGms") {
      if (raw === "" || gmsFormat.test(raw)) {
        setValues((v) => ({ ...v, [field]: raw }));
      }
      return;
    }
    setValues((v) => ({ ...v, [field]: raw }));
  }

  function handleGoTo() {
    let lng: number | null = null;
    let lat: number | null = null;

    if (format === "gdec") {
      if (!values.lngGdec || !values.latGdec) {
        notifications.show({ color: "yellow", message: "Preencha longitude e latitude." });
        return;
      }
      lng = parseFloat(values.lngGdec.replace(",", "."));
      lat = parseFloat(values.latGdec.replace(",", "."));
      if (isNaN(lng) || isNaN(lat)) {
        notifications.show({
          color: "yellow",
          message: "Coordenada informada é inválida.",
        });
        return;
      }
    } else {
      if (!values.lngGms || !values.latGms) {
        notifications.show({ color: "yellow", message: "Preencha longitude e latitude." });
        return;
      }
      lng = gmsToGdec(values.lngGms);
      lat = gmsToGdec(values.latGms);
      if (lng === null || lat === null || isNaN(lng) || isNaN(lat)) {
        notifications.show({
          color: "yellow",
          message: "Coordenada informada é inválida.",
        });
        return;
      }
    }

    let center: number[] = [lng, lat];
    center = GeomUtils.limitWorldCoordinates(center);

    if (Math.abs(center[1]) > 85) {
      notifications.show({
        color: "yellow",
        message:
          "Coordenada informada se encontra em um dos polos excedendo o limite do mapa.",
      });
      return;
    }

    center = GeomUtils.toWebMercator(center);

    if (setMarker) setMarker(center);

    const mapView = mapRef.current?.getView();
    if (mapView) {
      mapView.cancelAnimations();
      mapView.animate({ zoom: 18, center, duration: 250 });
    }
  }

  const content = (
    <Stack gap="xs" style={{ minWidth: 260 }}>
      <Group>
        <Radio.Group value={format} onChange={(v) => setFormat(v as CoordFormat)}>
          <Group gap="sm">
            <Radio value="gdec" label="GDEC" />
            <Radio value="gms" label="GMS" />
          </Group>
        </Radio.Group>
      </Group>

      {format === "gdec" ? (
        <>
          <TextInput
            label="Longitude"
            placeholder="-0,000000"
            value={values.lngGdec}
            onChange={(e) => handleChange("lngGdec", e.currentTarget.value)}
          />
          <TextInput
            label="Latitude"
            placeholder="-0,000000"
            value={values.latGdec}
            onChange={(e) => handleChange("latGdec", e.currentTarget.value)}
          />
        </>
      ) : (
        <>
          <TextInput
            label="Longitude"
            placeholder="-00:00:00,0000"
            value={values.lngGms}
            onChange={(e) => handleChange("lngGms", e.currentTarget.value)}
          />
          <TextInput
            label="Latitude"
            placeholder="-00:00:00,0000"
            value={values.latGms}
            onChange={(e) => handleChange("latGms", e.currentTarget.value)}
          />
        </>
      )}

      <Button onClick={handleGoTo} size="sm" style={{ alignSelf: "flex-end" }}>
        Ir para
      </Button>
    </Stack>
  );

  return (
    <div>
      <div className="ol-control ol-unselectable btn-goto-coordinates">
        <Popover
          opened={opened}
          onClose={handleClose}
          position="right-start"
          withArrow
          shadow="md"
        >
          <Popover.Target>
            <button
              title="Zoom para Coordenada"
              onClick={() => (opened ? handleClose() : handleOpen())}
            >
              <FontAwesomeIcon icon="crosshairs" />
            </button>
          </Popover.Target>
          <Popover.Dropdown>
            <Group justify="space-between" mb="xs">
              <span className="header">Zoom para Coordenada</span>
              <a
                href="#!"
                onClick={(e) => {
                  e.preventDefault();
                  handleClose();
                }}
              >
                <FontAwesomeIcon icon="times" />
              </a>
            </Group>
            <div className="content-goto-coordinates">{content}</div>
          </Popover.Dropdown>
        </Popover>
      </div>
    </div>
  );
}
